import React from 'react';
import {Market} from 'types';
import {MarketNamingTemplateDescriptions} from './MarketNamingTemplateDescriptions';
import {
	useInferredFreeTextMode,
	useMarketNamingTemplateValidation,
} from './hooks';
import {
	SeparatorCompat as Separator,
	ToggleCompat as Toggle,
} from 'components/FluentUICompat';

type MarketProps = keyof Market;

export type MarketNamingTemplateKeys = Extract<
	MarketProps,
	'regulationNamingTemplates' | 'regulatoryDocumentNamingTemplates'
>;

export type InputMarketProps = Pick<
	Market,
	| 'id'
	| 'name'
	| 'regulationNamingTemplates'
	| 'regulatoryDocumentNamingTemplates'
>;

type MarketNamingTemplateFieldChildProps = {
	validateFieldValue: (value: string) => boolean | string;
	isMarketSelected: boolean;
	placeholder: string;
};

type MarketNamingTemplateFieldProps = {
	markets: InputMarketProps[];
	namingHint: string;
	templatesToUse: MarketNamingTemplateKeys;
	label: string;
	savedValue?: string | null;
	savedValueKey?: string | null;
	children: (props: MarketNamingTemplateFieldChildProps) => React.ReactNode;
};

export const MarketNamingTemplateField: React.FC<
	MarketNamingTemplateFieldProps
> = ({
	markets,
	namingHint,
	templatesToUse,
	label,
	savedValue,
	savedValueKey,
	children,
}) => {
	const {validateFieldValue, isMarketSelected, placeholder} =
		useMarketNamingTemplateValidation(markets, namingHint, templatesToUse);
	const [freeText, setFreeText] = useInferredFreeTextMode(
		markets,
		savedValue,
		templatesToUse,
		savedValueKey,
	);

	const validateFieldValueFinal = (value: string) =>
		freeText || validateFieldValue(value);

	return (
		<React.Fragment>
			{children({
				validateFieldValue: validateFieldValueFinal,
				isMarketSelected,
				placeholder,
			})}
			<MarketNamingTemplateDescriptions
				markets={markets}
				templatesToUse={templatesToUse}
			/>
			{markets.length > 0 && (
				<>
					<Toggle
						label={label}
						checked={freeText}
						onChange={(_, checked) => setFreeText(Boolean(checked))}
					/>
					<Separator />
				</>
			)}
		</React.Fragment>
	);
};
